import { getIn } from 'final-form';
import styles from './ProvisionalStatement.module.scss';
import { financialYearLabel } from './RevenueDetails';
import { useForm } from 'react-final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import { dividerMetrics, generateProvisionalStatement, getGridInvalidRange, metricsName } from './revenueHelper';
import moment from 'moment';
import { Cell, Column, NonEditableCell, NumberCell, ReactGrid, StyledRange, TextCell } from '@silevis/reactgrid';
import { FieldArray } from 'react-final-form-arrays';
import { revenueGridStyles } from './revenueGridStyles';
import { CustomNumberCell } from '../components/ReactGrid/CustomNumberCell';
import { shortcutType } from '../components/ReactGrid/reactGridHelper';
import _ from 'lodash';
import ShortcutModal from '../components/ReactGrid/ShortcutModal';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { BsFillExclamationCircleFill as WarningIcon } from 'react-icons/bs'
import { Modal } from 'reactstrap';
import { underwritingFormWarningType } from '../../../interfaces/interfaces';
import { CustomTextCell } from '../components/ReactGrid/CustomTextCell';
import { RiAddFill as AddIcon } from 'react-icons/ri'

type Props = {
    fieldName: string,
    financialDetails: any;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}

const getRows = (provisionalStatement) => {
    if (!provisionalStatement) return [];
    const headerRow = {
        rowIndex: 0,
        height: 42,
    }
    const rowData = provisionalStatement.rowData.map((_, rowIndex) => ({
        rowIndex: rowIndex + 1,
        height: 42,
    }));

    return [headerRow, ...rowData];
}


const generateCells = (provisionalStatement, updateCellValue, addRow, deleteRow) => {
    if (!provisionalStatement) return [];

    const headerCells = provisionalStatement.headers.map((header, colIndex) => {
        const justifyContent = colIndex === 0 ? "flex-start" : "flex-end";
        const formattedHeader = colIndex == 0 ? header : moment(header, "MM-YYYY").format("MMM YYYY")
        return {
            rowIndex: 0,
            colIndex,
            isSelectable: false,
            isFocusable: false,
            Template: NonEditableCell,
            props: {
                value: formattedHeader,
                style: {
                    backgroundColor: "#F9F9F9",
                    paddingTop: "0.75rem",
                    paddingLeft: "0.5rem",
                    paddingRight: "0.5rem",
                    paddingBottom: "0.75rem",
                    justifyContent: justifyContent,
                    // transition: "background-color 0.2s ease-in-out"
                }
            },
        }
    });
    let isAfterLoanServicing = false;
    const dataCells = provisionalStatement.rowData.flatMap((row, rowIndex) => {
        const isDividerRow = row?.length && dividerMetrics.has(row[0]) ? true : false;
        const previousRow = provisionalStatement?.rowData[rowIndex - 1];
        if (previousRow?.length && previousRow[0] == metricsName.loanServicing) {
            isAfterLoanServicing = true;
        }
        return row.map((cellValue, colIndex) => {
            if ((colIndex === 0 || isDividerRow) && !isAfterLoanServicing) {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: NonEditableCell,
                    props: {
                        value: cellValue,
                        style: isDividerRow ? {
                            backgroundColor: "#F9F9F9",
                            paddingLeft: "0.5rem",
                            justifyContent: "flex-start",
                            fontWeight: '600'
                        } : {
                            paddingLeft: "2rem",
                            justifyContent: "flex-start"
                        }
                    }
                }
            } else {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: colIndex > 0 ? CustomNumberCell : CustomTextCell,
                    props: colIndex > 0 ? {
                        value: cellValue,
                        // hideZero: true,
                        onValueChanged: (newValue) => updateCellValue(`[${rowIndex}][${colIndex}]`, newValue),
                        onAddRow: isAfterLoanServicing ? (newRowIndex) => addRow(newRowIndex) : undefined,
                        onDeleteRow: isAfterLoanServicing ? (newRowIndex) => deleteRow(newRowIndex) : undefined,
                        allowShortcuts: [shortcutType.addRow, shortcutType.deleteRow],
                    } : {
                        text: cellValue,
                        onTextChanged: (newText) =>
                            updateCellValue(`[${rowIndex}][${colIndex}]`, newText),
                        onAddRow: isAfterLoanServicing ? (newRowIndex) => addRow(newRowIndex) : undefined,
                        onDeleteRow: isAfterLoanServicing ? (newRowIndex) => deleteRow(newRowIndex) : undefined,
                        allowShortcuts: [shortcutType.addRow, shortcutType.deleteRow],
                        style: {
                            paddingLeft: "2rem",
                            justifyContent: "flex-start"
                        }
                    },
                }
            }
        })
    }
    );

    return [...headerCells, ...dataCells];
}

const ProvisionalStatement = ({ fieldName, financialDetails, isOpen, setIsOpen }: Props) => {
    const { getState, change } = useForm();
    const [gridKey, setGridKey] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const provisionalStatement = getIn(getState()?.values, `${fieldName}.provisionalStatement`);

    const [isShorcutModalOpen, setIsShorcutModalOpen] = useState(false);
    const [warning, setWarning] = useState({
        state: false,
        type: null
    });

    // console.log('provisionalStatement', provisionalStatement)
    const [gridData, setGridData] = useState({
        headers: [],
        rowData: []
    });

    // const [styledRange, setStyledRange] = useState([]);
    const [validation, setValidation] = useState({
        message: '',
        styledRange: []
    });

    const [initialGridData, setInitialGridData] = useState(null);

    const gridContainerRef = useRef<HTMLDivElement>(null); // Create a ref

    // Function to check if the scrollbar is at the bottom
    const isScrollbarAtBottom = () => {
        const container = gridContainerRef.current;
        if (!container) return false;

        console.log('scroll top + container', container.scrollTop + container.clientHeight)
        console.log('container scroll height', container.scrollHeight);

        const buffer = 5;

        return container.scrollTop + container.clientHeight >= container.scrollHeight;
    };

    // Function to scroll to bottom
    const scrollToBottom = () => {
        const container = gridContainerRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: "smooth" // Enables smooth scrolling
            });
        }
    };



    useEffect(() => {
        if (isOpen) {
            if (!provisionalStatement) {
                const endDate = financialDetails?.fyPassed ? financialDetails?.fyEnd : moment().subtract('1', "month").format("DD-MM-YYYY");
                const startDate = financialDetails?.fyPassed ? financialDetails?.fyStart : moment().subtract('1', "year").format("DD-MM-YYYY");
                const provisionalStatementData = generateProvisionalStatement(startDate, endDate);
                setGridData({
                    headers: provisionalStatementData.headers,
                    rowData: provisionalStatementData.rowData
                })

                setInitialGridData(_.cloneDeep({
                    headers: provisionalStatementData.headers,
                    rowData: provisionalStatementData.rowData
                }))
            } else {
                setGridData({
                    headers: provisionalStatement.headers,
                    rowData: provisionalStatement.rowData
                })
            }
        }
    }, [isOpen]);

    const addRow = (rowIndex: number, isScrollToBottom: boolean = false) => {
        const shouldScroll = isScrollbarAtBottom();

        console.log('shouldScroll', shouldScroll)
        setGridData((prev) => {
            if (!prev || !prev.rowData) return;

            console.log(`Triggering addRow at index: ${rowIndex}`);
            console.log('Before splice:', prev.rowData.length);

            // Deep copy row data to trigger React state update
            const newStatement = [...prev.rowData.map(row => [...row])];

            console.log("Before splice:", newStatement.length);

            // Create a new row with the same number of columns, initialized as empty
            const newRow = Array(columns.length).fill("");

            // Insert new row above the current rowIndex
            newStatement.splice(rowIndex, 0, newRow);
            // setGridKey((prevKey) => prevKey + 1);  // 🔄 Force re-render

            console.log("After splice:", newStatement.length);

            setTimeout(() => {
                if (!shouldScroll && isScrollToBottom) scrollToBottom(); // Scroll only if was at bottom before adding
            }, 100);

            return {
                ...prev,
                rowData: newStatement,
            }
        });
    };

    const deleteRow = (rowIndex: number) => {
        setGridData((prev) => {
            if (!prev || prev.rowData.length <= 1) return;

            console.log(`Triggering deleteRow at index: ${rowIndex}`);

            // Remove row from the statement
            const newStatement = prev.rowData.filter((_, i) => i !== rowIndex);

            console.log("After delete:", newStatement.length);
            return {
                ...prev,
                rowData: newStatement,
            }
        });
    };


    const updateCellValue = (path: string, value) => {
        setGridData((prev) => {
            console.log('path', path)
            const newGridData = _.cloneDeep(prev?.rowData);
            _.set(newGridData, path, value);

            const [rowIndex, colIndex] = path.match(/\d+/g).map(Number);

            if (value.toString().trim() != "" && isSubmitted) {
                // ✅ Remove from styledRanges if it exists
                setValidation((prevStyledRanges) => {

                    const filteredRange = prevStyledRanges?.styledRange.filter(
                        (range) =>
                            !(range.range.start.rowIndex === rowIndex + 1 &&
                                range.range.start.columnIndex === colIndex)
                    )
                    return {
                        message: filteredRange.length ? 'Some cells are missing data.' : '',
                        styledRange: filteredRange
                    }
                });
            }

            return {
                ...prev,
                rowData: newGridData
            };
        })
    }
    const allColumns = new Array(13).fill(0);
    const columns = allColumns.map((_, colIndex) => ({
        colIndex,
        width: colIndex === 0 ? 220 : 120,
    }));

    // const rows = useMemo(() => {
    //     return gridData.rowData.map((_, rowIndex) => ({
    //         rowIndex,
    //         height: 42,
    //     }));
    // }, [gridData]);
    const rows = getRows(gridData);

    // const cells = useMemo(() => {
    //     return generateCells(gridData, updateCellValue, addRow, deleteRow);
    // }, [gridData]);
    const cells = generateCells(gridData, updateCellValue, addRow, deleteRow);

    const validateGrid = () => {
        const newStyledRanges = getGridInvalidRange(gridData);
        console.log('newStyledRanges', newStyledRanges)
        // setStyledRange(newStyledRanges);
        if (newStyledRanges.length) {
            setValidation({
                message: 'Some cells are missing data.',
                styledRange: newStyledRanges
            })
        } else {
            setValidation({
                message: '',
                styledRange: []
            })
        }
        return newStyledRanges.length === 0; // Returns true if grid is valid
    };

    return (
        <div className={styles.provisionalRootContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.leftContainer}>
                    <div className={styles.title}>
                        Provisional P&L Statement - {`${financialDetails?.fyPassed ? `FY ${financialYearLabel(financialDetails?.financialYear)}` : `Last 12 Months`}`}
                    </div>
                </div>
                <div className={styles.rightContainer}>
                    <MeritoButton
                        btnText="Save"
                        type='primary-1'
                        onClick={() => {
                            // save the provisional statement
                            const isValid = validateGrid();
                            setIsSubmitted(true);
                            if (isValid) {
                                change(`${fieldName}.provisionalStatement`, gridData);
                                setIsOpen(false);
                            } else {
                                setWarning({
                                    state: true,
                                    type: underwritingFormWarningType.save
                                })
                            }
                        }}
                    />
                    <MeritoButton
                        btnText="Cancel"
                        type='secondary-1'
                        onClick={() => {
                            // setIsOpen(false)
                            const stringifiedPrevData = JSON.stringify(initialGridData);
                            const stringifiedNewData = JSON.stringify(gridData);
                            if (stringifiedPrevData !== stringifiedNewData) {
                                setWarning({
                                    state: true,
                                    type: underwritingFormWarningType.cancel
                                });
                            } else {
                                setIsOpen(false)
                            }
                        }}
                    />
                </div>
            </div>
            <div className={styles.tipsContainer}>
                <div className={styles.tipsTitle}>
                    Tips:
                </div>
                <div className={styles.tipsContent}>
                    <ol>
                        <li>Enter values in INR Lakhs.</li>
                        <li>If you don’t have data for a cell, enter “0”.</li>
                        <li>For "Loan Servicing," provide monthly loan instalments and update the name of the NBFC/bank in the first column.</li>
                        <li>You can use these <span onClick={() => {
                            setIsShorcutModalOpen(true)
                        }}>spreadsheet shortcuts.</span></li>
                    </ol>
                </div>
            </div>

            <div className={styles.tableRootContainer} ref={gridContainerRef}>
                {gridData?.rowData?.length ? <ReactGrid
                    id={`${fieldName}.provisionalStatement`}
                    styledRanges={validation.styledRange}
                    stickyLeftColumns={1} stickyTopRows={1} styles={revenueGridStyles?.rgStyles} rows={rows} columns={columns} cells={cells} /> : null}
            </div>
            {validation.message ? <div className={styles.errorMessage}>
                {validation.message}
            </div> : null}

            <div className={styles.btnContainer}>
                <MeritoButton
                    type="secondary-1"
                    size="md"
                    icon={<AddIcon />}
                    btnText="Add New Loan"
                    onClick={() => {
                        addRow(gridData.rowData.length, true);
                    }}
                />
            </div>

            <ShortcutModal isOpen={isShorcutModalOpen} setIsOpen={setIsShorcutModalOpen}
                showShortcuts={[shortcutType.addRow, shortcutType.deleteRow, shortcutType.cut, shortcutType.copy, shortcutType.paste, shortcutType.moveLeft, shortcutType.moveRight]} />


            <Modal
                size={"md"}
                isOpen={warning?.state}
                toggle={() => setWarning({
                    state: false,
                    type: null
                })}
                centered={true}
                style={{ width: '450px' }}
                contentClassName='shortcut_modal'
            >
                <div className={styles.confirmationRootContainer}>
                    <div className={styles.topContainer}>
                        <div className={styles.iconContainer}>
                            <WarningIcon />
                        </div>
                        <div className={styles.messageContainer}>
                            Unsaved Changes Will Be Lost!
                        </div>
                    </div>

                    <div className={styles.bottomContainer}>
                        {warning?.type === underwritingFormWarningType.save ?
                            <>
                                <MeritoButton
                                    btnText={'Save & Continue'}
                                    size='lg'
                                    isRounded={true}
                                    type='secondary-2'
                                    onClick={() => {
                                        change(fieldName, gridData);
                                        setWarning({
                                            state: false,
                                            type: null
                                        })
                                        setIsOpen(false);
                                    }}
                                />
                                <MeritoButton
                                    btnText="Continue Editing"
                                    size='lg'
                                    isRounded={true}
                                    type='primary-1'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        });
                                    }}
                                />
                            </>
                            :
                            <>
                                <MeritoButton
                                    btnText={'Delete Changes'}
                                    size='lg'
                                    isRounded={true}
                                    type='secondary-2'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        })
                                        setIsOpen(false);
                                    }}
                                />
                                <MeritoButton
                                    btnText="Continue Editing"
                                    size='lg'
                                    isRounded={true}
                                    type='primary-1'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        });
                                    }}
                                />
                            </>
                        }
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default ProvisionalStatement;